<template>
  <div v-if="!discount">
    <div class="text-end">
      <AppBtn
        v-if="!inputVisible"
        size="small"
        prepend-icon="mdi:coupon-outline"
        @click="inputVisible = true"
      >
        {{ $tt('use_coupon') }}
      </AppBtn>
    </div>

    <v-expand-transition>
      <v-form v-if="inputVisible" :ref="setFormRef" @submit.prevent="submitCode">
        <div class="flex gap-4">
          <div class="inputs pb-0">
            <TextField v-model="input" :label="$tt('coupon_code')" :rules="[required]" />
            <AppBtn primary :loading prepend-icon="mdi:check" type="submit">
              {{ $tt('apply_coupon') }}
            </AppBtn>
          </div>
          <CloseBtn @click="inputVisible = false" />
        </div>
      </v-form>
    </v-expand-transition>
  </div>

  <div v-else>
    <AppIcon icon="mdi:coupon-outline" start />
    <strong class="me-1">{{ discount.coupon }}</strong>
    <DiscountChip :discount="discount" class="me-5" />
    <AppBtn color="error" size="x-small" @click="clear">
      {{ $tt('remove') }}
    </AppBtn>
  </div>
</template>

<script setup lang="ts">
import { COUPON_COOKIE } from '~/assets/constants';
import { AppError } from '~/helpers/error';
import { getDiscount } from '~/helpers/http';
import type { VisaForm } from '~/types';

const discount = defineModel<VisaForm['discount']>({ required: true });

const { setFormRef, ready, submit, loading } = useForm();
const { required } = useValidation();
const { getQueryParam } = useRouting();
const cookie = useCookie(COUPON_COOKIE);

const input = ref(getQueryParam('coupon') || cookie.value || '');
const inputVisible = ref(false);

watch(ready, submitCode, { once: true });
watch(discount, d => cookie.value = d ? d.coupon : null);

onMounted(() => {
  inputVisible.value = Boolean(input.value || discount.value);
});

function submitCode() {
  submit(set, {
    successMsg: 'coupon_applied',
    activeValidation: true
  });
}

async function set() {
  const response = await getDiscount(input.value).catch(() => null);
  if (!response) throw new AppError('bad_coupon');
  if (response.error) throw new AppError(response.error);
  discount.value = {
    coupon: input.value,
    percent: response.discount
  };
}

function clear() {
  discount.value = undefined;
}
</script>

<style scoped>
.inputs {
  display: flex;
  gap: var(--space-4);
  width: 100%;
}

@media (max-width: 850px) {
  .inputs {
    flex-direction: column;
    gap: 0;
  }
}
</style>